// Pure SEO-metadata computation for every public page. No React, no DOM.
// Shared by: the client head updater (usePageHead), the prerender script, and
// the sitemap generator — so canonical/hreflang are guaranteed consistent.

import { ACTIVE_VARIANTS, GameVariant } from '@draughts/shared';
import { TOURNAMENT_RULE_SECTIONS } from '../rules/tournamentRules';
import {
  BASE_URL,
  DEFAULT_LANG,
  RULES_LANGS,
  RulesLang,
  ContentPage,
  buildRulesIndexPath,
  buildGameRulesPath,
  buildRulesPath,
  buildOpeningsPath,
  buildCapturePath,
  buildTournamentRulesPath,
  buildContentPath,
  buildLandingPath,
} from './routes';

/** Minimal translator shape — satisfied by i18next's `t` / `getFixedT`. */
export type TFunc = (key: string, options?: Record<string, unknown>) => string;

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

export interface PageMeta {
  title: string;
  description: string;
  canonical: string;
  alternates: HreflangAlternate[];
  og: { title: string; description: string; url: string; locale: string; type: string };
  jsonLd: Record<string, unknown>;
}

/** Back-compat alias — the rules pages were the first users of this module. */
export type RulesMeta = PageMeta;

// --- Site-wide constants ----------------------------------------------------

export const SITE_NAME = 'Draughts Game';
export const SITE_LOGO = `${BASE_URL}/pwa-512x512.png`;
export const SITE_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * `og:locale` wants a full BCP-47_REGION pair, not a bare language: `it` is not
 * a valid value and scrapers that validate it simply drop the tag. Kept next to
 * the languages themselves so adding one forces a choice here too.
 */
const OG_LOCALE: Record<RulesLang, string> = {
  en: 'en_US',
  it: 'it_IT',
  nl: 'nl_NL',
  de: 'de_DE',
  fr: 'fr_FR',
  es: 'es_ES',
  pl: 'pl_PL',
  'pt-BR': 'pt_BR',
};

function pathFor(
  lang: RulesLang,
  variant: GameVariant | null,
  openings = false,
  capture = false,
): string {
  if (!variant) return buildRulesIndexPath(lang);
  if (capture) return buildCapturePath(lang, variant);
  return openings ? buildOpeningsPath(lang, variant) : buildRulesPath(lang, variant);
}

/**
 * All hreflang alternates for a rules page concept (index, a variant, its opening
 * tables, its capture rules). Every sub-page MUST pass its own flag: reusing
 * the variant alternates would point each language at the wrong URL — invisible
 * in the browser, fatal for the SEO the prerender exists for.
 */
export function alternatesFor(
  variant: GameVariant | null,
  openings = false,
  capture = false,
): HreflangAlternate[] {
  const list: HreflangAlternate[] = RULES_LANGS.map((lang) => ({
    hreflang: lang,
    href: BASE_URL + pathFor(lang, variant, openings, capture),
  }));
  list.push({ hreflang: 'x-default', href: BASE_URL + pathFor(DEFAULT_LANG, variant, openings, capture) });
  return list;
}

/** The same, for the non-rules content pages (landing, FAQ, legal). */
export function alternatesForPage(page: ContentPage): HreflangAlternate[] {
  const list: HreflangAlternate[] = RULES_LANGS.map((lang) => ({
    hreflang: lang,
    href: BASE_URL + buildContentPath(lang, page),
  }));
  list.push({ hreflang: 'x-default', href: BASE_URL + buildContentPath(DEFAULT_LANG, page) });
  return list;
}

/**
 * The same, for the game-rules page — l'elenco delle varianti. Come la pagina
 * dei tornei non appartiene a nessuna variante, quindi nessuna delle due
 * funzioni qui sopra sa costruirla: passarcela dentro punterebbe ogni lingua a
 * un URL che non viene pubblicato.
 */
export function alternatesForGameRules(): HreflangAlternate[] {
  const list: HreflangAlternate[] = RULES_LANGS.map((lang) => ({
    hreflang: lang,
    href: BASE_URL + buildGameRulesPath(lang),
  }));
  list.push({ hreflang: 'x-default', href: BASE_URL + buildGameRulesPath(DEFAULT_LANG) });
  return list;
}

/**
 * The same, for the tournament-rules page. It lives under the rules index but
 * belongs to no variant, so neither `alternatesFor` nor `alternatesForPage`
 * can build it — passing it through either would point every language at a URL
 * that is not published.
 */
export function alternatesForTournamentRules(): HreflangAlternate[] {
  const list: HreflangAlternate[] = RULES_LANGS.map((lang) => ({
    hreflang: lang,
    href: BASE_URL + buildTournamentRulesPath(lang),
  }));
  list.push({ hreflang: 'x-default', href: BASE_URL + buildTournamentRulesPath(DEFAULT_LANG) });
  return list;
}

// --- Structured data --------------------------------------------------------

function breadcrumbList(items: { name: string; url: string }[]): Record<string, unknown> {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/** One `@graph` wrapper, so a page can carry its main entity AND its breadcrumb. */
function graph(nodes: Record<string, unknown>[]): Record<string, unknown> {
  return { '@context': 'https://schema.org', '@graph': nodes };
}

/**
 * The organisation and the site itself. Emitted on the landing only: repeating
 * it on all 288 pages would say nothing new, and Google reads it once anyway.
 *
 * No `SearchAction`: there is no site search, and declaring one that 404s is a
 * false claim about the site, not a missing feature.
 */
function siteNodes(lang: RulesLang, canonical: string, description: string): Record<string, unknown>[] {
  return [
    {
      '@type': 'Organization',
      '@id': `${BASE_URL}/#organization`,
      name: SITE_NAME,
      url: `${BASE_URL}/`,
      logo: SITE_LOGO,
    },
    {
      '@type': 'WebSite',
      '@id': `${BASE_URL}/#website`,
      name: SITE_NAME,
      url: `${BASE_URL}/`,
      description,
      inLanguage: lang,
      publisher: { '@id': `${BASE_URL}/#organization` },
    },
    {
      '@type': 'VideoGame',
      '@id': `${BASE_URL}/#game`,
      name: SITE_NAME,
      url: canonical,
      description,
      inLanguage: lang,
      genre: 'board game',
      applicationCategory: 'GameApplication',
      operatingSystem: 'Any',
      gamePlatform: ['Web browser', 'Android', 'iOS'],
      playMode: ['SinglePlayer', 'MultiPlayer'],
      publisher: { '@id': `${BASE_URL}/#organization` },
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', availability: 'https://schema.org/InStock' },
    },
  ];
}

/** i18next returns the raw array for `returnObjects`, outside the `string` contract. */
function translateObject(t: TFunc, key: string): unknown {
  return (t as unknown as (k: string, o?: Record<string, unknown>) => unknown)(key, {
    returnObjects: true,
  });
}

// --- Rules pages ------------------------------------------------------------

/**
 * I tre anelli fissi della briciola della sezione regole, in un posto solo: le
 * usano tutte e tre le funzioni qui sotto, e tre copie della stessa riga erano
 * tre occasioni di farle divergere.
 *
 * `gameRules` sta in mezzo a `rules` e alla variante perche' e' la gerarchia
 * vera: l'elenco delle varianti E' il genitore di una pagina variante, e l'hub
 * e' il genitore delle due sottosezioni. L'URL di una variante resta fratello
 * di quello dell'elenco — la briciola dichiara la logica del sito, non la
 * scomposizione del path.
 *
 * Il primo anello ha una chiave sua, `seo.breadcrumbHome`, e non quella della
 * voce di barra: la barra porta la stessa parola in tutte le lingue perche'
 * deve stare in una casella da 72px sul telefono, mentre qui il nome e' quello
 * che il motore di ricerca mostra, e la parola della lingua vale piu' della
 * brevita'. Due usi, due vincoli opposti, due chiavi.
 */
function rulesCrumbs(lang: RulesLang, t: TFunc) {
  return {
    home: { name: t('seo.breadcrumbHome'), url: BASE_URL + buildLandingPath(lang) },
    rules: { name: t('rules.eyebrow'), url: BASE_URL + buildRulesIndexPath(lang) },
    gameRules: { name: t('rules.gameRulesHead'), url: BASE_URL + buildGameRulesPath(lang) },
  };
}

/**
 * @param variant  null → the rules index page; otherwise a per-variant page.
 * @param t        translator already bound to `lang`.
 * @param openings true → the variant's opening-tables sub-page.
 * @param capture  true → the variant's detailed capture-rules sub-page.
 */
export function computeRulesMeta(
  lang: RulesLang,
  variant: GameVariant | null,
  t: TFunc,
  openings = false,
  capture = false,
): PageMeta {
  const canonical = BASE_URL + pathFor(lang, variant, openings, capture);
  const alternates = alternatesFor(variant, openings, capture);
  const draughtsWord = t('rules.draughtsWord');
  const og = (title: string, description: string) => ({
    title,
    description,
    url: canonical,
    locale: OG_LOCALE[lang],
    type: 'article',
  });

  const { home: crumbHome, rules: crumbRules, gameRules: crumbGameRules } = rulesCrumbs(lang, t);

  if (!variant) {
    const title = t('rules.seo.indexTitle', { draughtsWord });
    const description = t('rules.seo.indexDescription', { draughtsWord });
    return {
      title,
      description,
      canonical,
      alternates,
      og: og(title, description),
      jsonLd: graph([
        {
          '@type': 'CollectionPage',
          name: title,
          description,
          url: canonical,
          inLanguage: lang,
          /*
           * Le due meta' della sezione, dichiarate: e' tutto quello che questa
           * pagina contiene. La raccolta delle ventiquattro varianti si e'
           * spostata sulla pagina delle regole di gioco, dove ora vive la
           * griglia — e con lei l'`ItemList` che prima non c'era.
           */
          hasPart: [
            {
              '@type': 'CollectionPage',
              name: t('rules.gameRulesHead'),
              url: BASE_URL + buildGameRulesPath(lang),
            },
            {
              '@type': 'Article',
              name: t('rules.tournaments.title'),
              url: BASE_URL + buildTournamentRulesPath(lang),
            },
          ],
        },
        breadcrumbList([crumbHome, crumbRules]),
      ]),
    };
  }

  const variantName = t(`game.variants.${variant.toLowerCase()}`);
  const title = capture
    ? t('rules.seo.captureTitle', { variant: variantName, draughtsWord })
    : openings
      ? t('rules.seo.openingsTitle', { variant: variantName, draughtsWord })
      : t('rules.seo.title', { variant: variantName, draughtsWord });
  const description = capture
    ? t('rules.seo.captureDescription', { variant: variantName, draughtsWord })
    : openings
      ? t('rules.seo.openingsDescription', { variant: variantName, draughtsWord })
      : t('rules.seo.description', { variant: variantName, draughtsWord });

  const crumbVariant = { name: variantName, url: BASE_URL + buildRulesPath(lang, variant) };

  // A step-by-step rule set is a HowTo, not a dataset and not the game page.
  if (capture) {
    return {
      title,
      description,
      canonical,
      alternates,
      og: og(title, description),
      jsonLd: graph([
        {
          '@type': 'HowTo',
          name: title,
          description,
          url: canonical,
          inLanguage: lang,
          about: { '@type': 'Game', name: variantName },
        },
        breadcrumbList([
          crumbHome,
          crumbRules,
          crumbGameRules,
          crumbVariant,
          { name: t('rules.capture.title'), url: canonical },
        ]),
      ]),
    };
  }

  // The tables are a dataset about the game, not the game page itself.
  if (openings) {
    return {
      title,
      description,
      canonical,
      alternates,
      og: og(title, description),
      jsonLd: graph([
        {
          '@type': 'Dataset',
          name: title,
          description,
          url: canonical,
          inLanguage: lang,
          about: { '@type': 'Game', name: variantName },
          creator: { '@type': 'Organization', name: 'Federazione Italiana Dama' },
          license: 'https://www.federdama.it/',
        },
        breadcrumbList([
          crumbHome,
          crumbRules,
          crumbGameRules,
          crumbVariant,
          { name: t('rules.openings.title'), url: canonical },
        ]),
      ]),
    };
  }

  return {
    title,
    description,
    canonical,
    alternates,
    og: og(title, description),
    jsonLd: graph([
      {
        '@type': 'Game',
        name: variantName,
        description,
        url: canonical,
        inLanguage: lang,
        genre: 'board game',
        gameItem: { '@type': 'Thing', name: draughtsWord },
      },
      breadcrumbList([crumbHome, crumbRules, crumbGameRules, crumbVariant]),
    ]),
  };
}

/**
 * Le regole di GIOCO: l'elenco delle ventiquattro varianti. Funzione sua e non
 * un quarto booleano posizionale su `computeRulesMeta` — l'avvertimento sopra
 * `computeTournamentRulesMeta` vale identico, e quella firma di booleani ne
 * porta gia' due.
 *
 * `CollectionPage` come prima, ma con un `ItemList` vero: le ventiquattro
 * pagine variante arrivano a Google come dati strutturati e non piu' solo come
 * tag `<a>`. Finche' questa raccolta stava sull'indice, l'`ItemList` non c'era.
 */
export function computeGameRulesMeta(lang: RulesLang, t: TFunc): PageMeta {
  const canonical = BASE_URL + buildGameRulesPath(lang);
  const draughtsWord = t('rules.draughtsWord');
  const title = t('rules.seo.gameTitle', { draughtsWord });
  const description = t('rules.seo.gameDescription', { draughtsWord });
  const { home, rules, gameRules } = rulesCrumbs(lang, t);

  return {
    title,
    description,
    canonical,
    alternates: alternatesForGameRules(),
    og: {
      title,
      description,
      url: canonical,
      locale: OG_LOCALE[lang],
      type: 'article',
    },
    jsonLd: graph([
      {
        '@type': 'CollectionPage',
        name: title,
        description,
        url: canonical,
        inLanguage: lang,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: ACTIVE_VARIANTS.length,
          itemListElement: ACTIVE_VARIANTS.map((v, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: t(`game.variants.${v.toLowerCase()}`),
            url: BASE_URL + buildRulesPath(lang, v),
          })),
        },
      },
      breadcrumbList([home, rules, gameRules]),
    ]),
  };
}

/**
 * The tournament rules — a sub-page of the rules index that belongs to no
 * variant, so it gets its own function rather than a third positional boolean
 * on `computeRulesMeta`. That signature already carries two, and the warning
 * above it (every sub-page MUST pass its own flag) is the reason not to add a
 * third: the next caller that forgets one publishes the wrong canonical.
 *
 * `Article` and not `FAQPage`: the nine chapters used to be FAQ entries, and
 * declaring them as questions again would both be false — they are headings
 * now, not questions — and duplicate the real `FAQPage` that still lives on
 * `/{lang}/{faq}`. Not `HowTo` either: the capture page is a procedure, this is
 * a regulation.
 */
export function computeTournamentRulesMeta(lang: RulesLang, t: TFunc): PageMeta {
  const canonical = BASE_URL + buildTournamentRulesPath(lang);
  const draughtsWord = t('rules.draughtsWord');
  const title = t('rules.seo.tournamentsTitle', { draughtsWord });
  const description = t('rules.seo.tournamentsDescription', { draughtsWord });
  const name = t('rules.tournaments.title');
  // Le stesse due di ogni pagina regole: l'hub resta il genitore diretto dei
  // tornei, che non passano dall'elenco delle varianti.
  const { home, rules } = rulesCrumbs(lang, t);

  return {
    title,
    description,
    canonical,
    alternates: alternatesForTournamentRules(),
    og: {
      title,
      description,
      url: canonical,
      locale: OG_LOCALE[lang],
      type: 'article',
    },
    jsonLd: graph([
      {
        '@type': 'Article',
        headline: title,
        description,
        url: canonical,
        inLanguage: lang,
        about: { '@type': 'Thing', name },
        articleSection: TOURNAMENT_RULE_SECTIONS.map((s) => t(s.titleKey)),
        publisher: { '@type': 'Organization', name: SITE_NAME, url: `${BASE_URL}/` },
      },
      breadcrumbList([home, rules, { name, url: canonical }]),
    ]),
  };
}

// --- Landing, FAQ, legal ----------------------------------------------------

export interface ContentPageOptions {
  /** ISO date from the markdown frontmatter — legal pages only. */
  dateModified?: string;
}

export function computePageMeta(
  lang: RulesLang,
  page: ContentPage,
  t: TFunc,
  options: ContentPageOptions = {},
): PageMeta {
  const canonical = BASE_URL + buildContentPath(lang, page);
  const alternates = alternatesForPage(page);
  const title = t(`seo.${page}.title`);
  const description = t(`seo.${page}.description`);
  const crumbHome = { name: t('seo.breadcrumbHome'), url: BASE_URL + buildLandingPath(lang) };
  const base = {
    title,
    description,
    canonical,
    alternates,
    og: {
      title,
      description,
      url: canonical,
      locale: OG_LOCALE[lang],
      type: page === 'landing' ? 'website' : 'article',
    },
  };

  if (page === 'landing') {
    return { ...base, jsonLd: graph(siteNodes(lang, canonical, description)) };
  }

  if (page === 'faq') {
    const entries = translateObject(t, 'faq.entries');
    const questions = Array.isArray(entries)
      ? entries
          .filter((e): e is { q: string; a: string } =>
            Boolean(e) && typeof (e as { q?: unknown }).q === 'string' && typeof (e as { a?: unknown }).a === 'string')
          .map((e) => ({
            '@type': 'Question',
            name: e.q,
            acceptedAnswer: { '@type': 'Answer', text: e.a },
          }))
      : [];
    return {
      ...base,
      jsonLd: graph([
        {
          '@type': 'FAQPage',
          name: title,
          description,
          url: canonical,
          inLanguage: lang,
          mainEntity: questions,
        },
        breadcrumbList([crumbHome, { name: title, url: canonical }]),
      ]),
    };
  }

  return {
    ...base,
    jsonLd: graph([
      {
        '@type': 'WebPage',
        name: title,
        description,
        url: canonical,
        inLanguage: lang,
        ...(options.dateModified ? { dateModified: options.dateModified } : {}),
        publisher: { '@type': 'Organization', name: SITE_NAME, url: `${BASE_URL}/` },
      },
      breadcrumbList([crumbHome, { name: title, url: canonical }]),
    ]),
  };
}
